"use client";
import { useRef } from "@/utils/hooks/reactUseCustomHooks";
import { algoliaClient } from "@dxp-feed/algolia";
import { searchBySingleIndex } from "@/services/algolia";
import { ALGOLIA_INDEXES } from "@/services/algolia/config";
import moment from "moment-timezone";

/**
 * Creates and returns an instance of the Algolia client.
 * @returns {object} Algolia client instance
 */
export const createAlgoliaClient = () =>
  algoliaClient(
    process.env.NEXT_PUBLIC_ALGOLIA_APP_ID ?? "",
    process.env.NEXT_PUBLIC_ALGOLIA_API_KEY ?? "",
    { indexName: ALGOLIA_INDEXES.STORES.PRIMARY_INDEX ?? "" }
  );

/** function to get store deatil by slug */
export const searchStoredByName = async (query: string) => {
  try {
    const params = {
      indexName: ALGOLIA_INDEXES.STORES.PRIMARY_INDEX,
      hitsPerPage: 10,
      searchParams: {
        filters: `slug:"${query}"`,
      },
    };
    const data = await searchBySingleIndex(params as any);
    return data?.hits[0] || null;
  } catch (error) {
    console.log(error);
  }
};
/**get store detail by number */

export const getStoreDetailByStoreNumber = async (storeNumber: any) => {
  try {
    const params = {
      indexName: ALGOLIA_INDEXES.STORES.PRIMARY_INDEX,
      hitsPerPage: 10,
      searchParams: {
        filters: `storeNumber:"${storeNumber}"`,
      },
    };
    const data = await searchBySingleIndex(params as any);
    return data?.hits[0] || null;
  } catch (error) {
    console.log(error);
  }
};

/**
 * Gets the store hours for the current day.
 * @param {object} storeHours - Object containing store hours for each day of the week.
 * @returns {string} Store hours for today.
 */

export const daysOfWeek = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];
export const provinceTimeZones: { [key: string]: string } = {
  AB: "America/Edmonton",
  BC: "America/Vancouver",
  MB: "America/Winnipeg",
  NB: "America/Moncton",
  NL: "America/St_Johns",
  NS: "America/Halifax",
  NT: "America/Yellowknife",
  NU: "America/Iqaluit",
  ON: "America/Toronto",
  PE: "America/Halifax",
  QC: "America/Toronto",
  SK: "America/Regina",
  YT: "America/Whitehorse",
};
export const getStoreHoursForToday = (
  storeHours?: { [key: string]: string } | null,
  provinceCode?: string,
  defaultTimeZone: string = "America/Toronto"
): string => {
  const timeZone =
    provinceCode && provinceTimeZones[provinceCode]
      ? provinceTimeZones[provinceCode]
      : defaultTimeZone;

  if (!storeHours || typeof storeHours !== "object") return "Closed";

  const todayIndex = moment().tz(timeZone).day();
  const today = daysOfWeek[todayIndex];
  const hoursToday = storeHours[today];

  if (!hoursToday || hoursToday.trim() === "") return "Closed";

  const [openRaw, closeRaw] = hoursToday.split("to").map((t) => t.trim());
  if (!openRaw || !closeRaw) return "Closed";

  const openTime = moment.tz(
    `${moment().format("YYYY-MM-DD")} ${openRaw}`,
    "YYYY-MM-DD h:mm A",
    timeZone
  );
  const closeTime = moment.tz(
    `${moment().format("YYYY-MM-DD")} ${closeRaw}`,
    "YYYY-MM-DD h:mm A",
    timeZone
  );
  const now = moment.tz(timeZone);

  if (now.isBefore(openTime) || now.isAfter(closeTime)) return "Closed now";

  const formatTime = (time: moment.Moment) => time.format("hA");

  return `Today · ${formatTime(openTime)} – ${formatTime(closeTime)}`;
};

/**
 * Determines if the store is currently open based on today's hours.
 * @param {string} hoursToday - Store hours for today in the format "HH:mm a.m. to HH:mm p.m.".
 * @returns {boolean} True if the store is open now, otherwise false.
 */

export const isStoreOpenNow = (
  hoursToday: string,
  timeZone: string = process.env.NEXT_PUBLIC_TIME_ZONE || "America/Toronto"
): boolean => {
  if (!hoursToday || hoursToday.trim().toLowerCase() === "closed") return false;

  // Improved parseTime to trim input and allow optional whitespace before AM/PM.
  const parseTime = (
    timeStr: string
  ): { hour: number; minute: number } | null => {
    const match = timeStr.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return null;

    const [, hour, minute, period] = match;
    let hourNum = Number(hour);
    const minuteNum = Number(minute);

    // Adjust for 12-hour format
    if (period.toUpperCase() === "PM" && hourNum !== 12) hourNum += 12;
    if (period.toUpperCase() === "AM" && hourNum === 12) hourNum = 0;

    return { hour: hourNum, minute: minuteNum };
  };

  // Split the hoursToday string and trim each part
  const [rawStart, rawEnd] = hoursToday.split(" - ");
  if (!rawStart || !rawEnd) return false;

  const startTime = parseTime(rawStart.trim());
  const endTime = parseTime(rawEnd.trim());
  if (!startTime || !endTime) return false;

  const now = moment.tz(timeZone);
  const openTime = moment.tz(timeZone).set({
    hour: startTime.hour,
    minute: startTime.minute,
    second: 0,
    millisecond: 0,
  });
  const closeTime = moment.tz(timeZone).set({
    hour: endTime.hour,
    minute: endTime.minute,
    second: 0,
    millisecond: 0,
  });

  // Handle close time as next day if it's before open time (e.g., 12:00 AM next day)
  if (closeTime.isBefore(openTime)) {
    closeTime.add(1, "day");
  }

  return now.isBetween(openTime, closeTime, undefined, "[)");
};

// Custom hook for debouncing
export const useDebounce = <T extends (...args: string[]) => void>(
  callback: T,
  delay: number
) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Return a function that calls the callback after debounce with the correct types
  return (...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};

export interface StoreHours {
  is24Hours: boolean;
  storeHours: {
    [day: string]: string;
  };
}

function parseTimeToMinutes(timeStr: string): number {
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(a\.m\.|p\.m\.)/i);
  if (!match) return -1;

  const [, hourStr, minuteStr, meridian] = match;
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  const lowerCaseMeridian = meridian.toLowerCase();
  if (lowerCaseMeridian === "p.m." && hour !== 12) hour += 12;
  if (lowerCaseMeridian === "a.m." && hour === 12) hour = 0;

  return hour * 60 + minute;
}

/**
 * Gets the current store status based on the store's operating hours and the current date and time.
 * @param storeHours - An object containing the store's operating hours and whether it's a 24/7 store.
 * @param date - A Date object representing the current date and time. Defaults to the current date if not provided.
 * @returns A string representing the store's current status: either "Open until [closing time]", "Closed", or "Open 24 Hours".
 */

export function getStoreStatusold(
  storeHours: { [key: string]: string } | null | undefined,
  date: Date = new Date(),
  timeZone: string = process.env.NEXT_PUBLIC_TIME_ZONE || "America/Toronto"
): string {
  const daysOfWeek = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ];

  if (
    !storeHours ||
    typeof storeHours !== "object" ||
    Object.entries(storeHours).every(([key, value]) => key.trim() === "" && value.trim() === "")
  ) {
    return "Closed";
  }
  const currentTimeInTimeZone = moment(date).tz(timeZone);
  const currentDay = daysOfWeek[currentTimeInTimeZone.day()];
  const currentTime =
    currentTimeInTimeZone.hours() * 60 + currentTimeInTimeZone.minutes();

  const hoursForDay = storeHours[currentDay]?.trim();

  if (!hoursForDay || hoursForDay.toLowerCase() === "closed") {
    return "Closed now";
  }

  const [openingTime, closingTime] = hoursForDay.split(" to ");

  if (!openingTime || !closingTime) {
    return "Closed now";
  }

  const openingMinutes = parseTimeToMinutes(openingTime);
  let closingMinutes = parseTimeToMinutes(closingTime);

  if (openingMinutes === -1 || closingMinutes === -1) {
    return "Closed now";
  }

  // Handle midnight case (e.g., "7:00 AM to 12:00 AM")
  if (closingMinutes === 0 && openingMinutes !== 0) {
    closingMinutes = 1440;
  }

  const isOpen = currentTime >= openingMinutes && currentTime < closingMinutes;

  const formatTime = (time: string): string => {
    return time
      .replace(/^0?(\d{1,2}):(\d{2})/, (_, h, m) => `${parseInt(h)}:${m}`)
      .replace(/\s*a\.m\./gi, " AM")
      .replace(/\s*p\.m\./gi, " PM")
      .trim();
  };

  return isOpen ? `Open until ${formatTime(closingTime)}` : "Closed now";
}

export const formatStoreHours = (hours: string): string => {
  return hours
    .replace(/\b0?(\d{1}):(\d{2})/g, (_, h, m) => `${parseInt(h)}:${m}`)
    .replace(/\s*to\s*/gi, " to ")
    .replace(/\b(a|p)\.m\./gi, (_, period) => `${period.toLowerCase()}.m`)
    .trim();
};

/**
 * Handles errors related to geolocation fetching.
 * This function provides different error messages based on the specific geolocation error code.
 * @param error - The geolocation error object containing the error code and message.
 * @returns void - This function doesn't return anything. It displays alerts based on the error code.
 */
export const handleGeolocationError = (
  error: GeolocationPositionError
): void => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      alert(
        "Location permission denied. Please enable location access in your browser settings."
      );
      break;
    case error.POSITION_UNAVAILABLE:
      alert("Location information is unavailable. Please try again.");
      break;
    case error.TIMEOUT:
      alert("The request to get your location timed out. Please try again.");
      break;
    default:
      alert("An unknown error occurred while fetching location.");
  }
};

// Define an interface for PlaceSuggestion
export interface PlaceSuggestion {
  description: string;
  place_id: string;
  latitude?: number;
  longitude?: number;
}

// Define an interface for the Google Maps Autocomplete Prediction
declare global {
  interface Window {
    google: any;
  }
}

/**
 * Fetches location suggestions based on a search query using the Google Maps Autocomplete service.
 * @param locationName - The name or partial name of the location to search for.
 * @returns A promise that resolves with an array of location suggestions, where each suggestion
 *          contains a description and a place_id.
 *          Example: [{ description: "Location Name", place_id: "somePlaceId" }]
 */
export const getLocationSuggestions = async (
  locationName: string
): Promise<{ description: string; place_id: string }[]> => {
  return new Promise((resolve) => {
    if (!window.google?.maps?.places) {
      console.error("Google Maps API is not loaded.");
      return resolve([]); // Resolve with empty array if the API is not loaded
    }

    const autocompleteService =
      new window.google.maps.places.AutocompleteService();

    autocompleteService.getPlacePredictions(
      {
        input: locationName,
        componentRestrictions: {
          country: process.env.NEXT_PUBLIC_GOOGLE_API_VALID_COUNTRY_NAME,
        },
      },
      (
        predictions: google.maps.places.AutocompletePrediction[] | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !predictions
        ) {
          return resolve([]);
        }

        const newSuggestions = predictions.map(({ description, place_id }) => ({
          description,
          place_id,
        }));

        resolve(newSuggestions); // Resolve the promise with the suggestions
      }
    );
  });
};

/**
 * Fetches detailed information about a place using its Place ID from the Google Maps Places API.
 * @param placeId - The unique identifier for the place in Google Maps.
 * @returns A promise that resolves with an object containing:
 *    - name: The name of the place (string).
 *    - address: The formatted address of the place (string).
 *    - latitude: The latitude of the place (number).
 *    - longitude: The longitude of the place (number).
 */
export const getPlaceDetails = async (
  placeId: string
): Promise<{
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}> => {
  return new Promise((resolve, reject) => {
    if (!window.google?.maps?.places) {
      console.error("Google Maps API is not loaded.");
      return reject("Google Maps API is not loaded.");
    }

    const placesService = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    placesService.getDetails(
      { placeId, fields: ["geometry.location", "name", "formatted_address"] },
      (
        place: google.maps.places.PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place?.geometry?.location
        ) {
          const { lat, lng } = place.geometry.location;
          resolve({
            name: place.name as string,
            address: place.formatted_address as string,
            latitude: lat(),
            longitude: lng(),
          });
        } else {
          reject("Failed to fetch place details.");
        }
      }
    );
  });
};

export const constructFilterQuery = (filters: string[]) => {
  return filters
    .map((filter) => {
      return `bannerCode:'${filter}'`;
    })
    .join(" OR "); // Join all filters with OR
};

export const constructAllFilterQuery = (
  storeTypes: string[] = [],
  departments: string[] = [],
  provinces: string[] = []
): string => {
  const defaultBanner = process.env.NEXT_PUBLIC_DEFAULT_HOME_BANNER || "";

  const escapeValue = (val: string) => val.replace(/'/g, "\\'");

  const storeTypeQuery = storeTypes.length
    ? `(${storeTypes.map((type) => `storeType: '${escapeValue(type)}'`).join(" OR ")})`
    : "";
  const departmentQuery = departments.length
    ? `(${departments.map((dept) => `departments.type: '${escapeValue(dept)}'`).join(" OR ")})`
    : "";
  const provinceQuery = provinces.length
    ? `(${provinces.map((prov) => `address.province: '${escapeValue(prov)}'`).join(" OR ")})`
    : "";
  const queryParts = [
    `bannerCode:'${escapeValue(defaultBanner)}'`,
    storeTypeQuery,
    departmentQuery,
    provinceQuery,
  ].filter(Boolean);

  return queryParts.join(" AND ");
};

// "(address.province:'AB' OR address.province:'hj') AND (bannerCode:'Sobeys' OR bannerCode:'Urban Fresh' bannerCode:'24 Hours') AND (departments.name:'Pharmacy' OR  departments.name:'xyx' )"

/*
  This function filters and formats holiday data to include only holidays within a 29-day period: 
  14 days before today, today, and 7 days after today.
  It also formats the holiday date and time values before returning the updated list of holidays.
  Returns an array of holidays with additional properties: formatted date, day of the week, and formatted time.
*/

export function getHolidaysWithDetails(
  holidayHours: Array<any>,
  timeZone: string = "America/Toronto" // unused now
) {
  if (!holidayHours || holidayHours.length === 0) return [];

  const pad = (n: number) => String(n).padStart(2, "0");

  const formatTime = (isoString: string | null) => {
    if (!isoString || isoString.toLowerCase() === "null") return "";

    const date = new Date(isoString);
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();

    const period = hours >= 12 ? "p.m" : "a.m";
    const formattedHour = hours % 12 === 0 ? 12 : hours % 12;

    return `${formattedHour}:${pad(minutes)} ${period}`;
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  };

  const getDayOfWeek = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      timeZone: "UTC",
    });
  };

  const getMonthName = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      timeZone: "UTC",
    });
  };

  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  const startDate = new Date(today);
  startDate.setUTCDate(today.getUTCDate() - 14);

  const endDate = new Date(today);
  endDate.setUTCDate(today.getUTCDate() + 14);

  return holidayHours
    .filter((holiday) => {
      const baseDate = new Date(holiday.date || holiday.openTime);
      baseDate.setUTCHours(0, 0, 0, 0);
      return baseDate >= startDate && baseDate <= endDate;
    })
    .map((holiday) => {
      const displayDate = new Date(holiday.date ?? holiday.openTime);
      return {
        ...holiday,
        dayOfWeek: getDayOfWeek(displayDate.toISOString()),
        formattedDate: formatDate(displayDate.toISOString()),
        monthName: getMonthName(displayDate.toISOString()),
        openTime: formatTime(holiday.openTime),
        closeTime: formatTime(holiday.closeTime),
        isClosed: holiday.isClosed || holiday.openTime === holiday.closeTime,
      };
    });
}

/**
 * Opens a Google Maps URL in a new tab using the provided coordinates and optional details.
 * @param {string | undefined} lat - Latitude coordinate (required).
 * @param {string | undefined} lng - Longitude coordinate (required).
 * @param {string} [locationName] - Optional name for the location (defaults to "Store Location").
 * @param {number} [zoom=20] - Optional zoom level (defaults to 20).
 */
export const handleRedirectOnMap = (
  lat: string | undefined,
  lng: string | undefined,
  locationName?: string,
  zoom: number = 20
): void => {
  try {
    const baseUrl = process.env.NEXT_PUBLIC_GOOGLE_MAPS_URL;
    if (lat && lng) {
      const mapsUrl = `${baseUrl}?q=${locationName ?? "Store Location"}+${lat},${lng}&z=${zoom}`;
      window.open(mapsUrl, "_blank");
    } else {
      console.error("Location data is missing");
    }
  } catch (error) {
    console.log("error during handleRedirectOnMap:", error);
  }
};
export const getOpeningHours = (
  hours: Record<string, string> | null | undefined
) => {
  if (
    !hours ||
    typeof hours !== "object" ||
    Array.isArray(hours) ||
    Object.keys(hours).length === 0
  ) {
    return [{ day: "N/A", time: "N/A" }];
  }

  const dayOrder = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ];

  const formatDay = (day: string) => {
    const lower = day.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1); // "MONDAY" → "Monday"
  };

  const formatTime = (time: string) =>
    time
      .replace(/\b0(\d):(\d{2})/g, "$1:$2") // Remove leading zero from hours
      .replace(/\s*a\.m\./gi, "AM")
      .replace(/\s*p\.m\./gi, "PM")
      .replace(/\s*to\s*/gi, " - "); // Normalize the time

  return dayOrder
    .filter((day) => hours[day]) // Only include days that exist in the API response
    .map((day) => ({
      day: formatDay(day),
      time: formatTime(hours[day]),
    }));
};

export const formatHours = (timeString: string): string => {
  if (!timeString || typeof timeString !== "string") {
    return "Closed";
  }
  const formattedTime = timeString
    .replace(/\./g, "")
    .replace(/\b(a|p)m\b/gi, (match) => match.toUpperCase())
    .replace(/\s+to\s+/gi, "-"); // Replacing " to " with "-"

  return formattedTime.match(/^\d{1,2}:\d{2} (AM|PM)-\d{1,2}:\d{2} (AM|PM)$/)
    ? formattedTime
    : timeString;
};

export const isToday = (
  day: string,
  provinceCode: string,
  timeZone?: string
): boolean => {
  // Use passed timeZone if provided, else get from provinceCode, else fallback
  const tz = timeZone || provinceTimeZones[provinceCode] || "America/Toronto";
  const today = moment().tz(tz).format("dddd"); // Full day name e.g. "Monday"
  return day.toLowerCase() === today.toLowerCase();
};

// Utility function to format time
export const changeTimeFormat = (time: string | undefined | null): string => {
  if (!time) return "N/A";

  return time
    .replace(/\b0(\d):(\d{2})/g, "$1:$2") // Remove leading zero from hour
    .replace(/a\.m\./gi, "AM")
    .replace(/p\.m\./gi, "PM")
    .replace(/\bto\b/gi, "-")
    .replace(/\s?–\s?/g, " – "); // Normalize dashes
};

export const STORE_TYPE_OPTIONS = ["24 Hours", "Sobeys", "Urban Fresh"];
export const DEPARTMENT_OPTIONS = [
  "Pharmacy",
  "Floral Department",
  "Bakery Department",
  "Seafood Department",
  "Meat Department",
  "Produce Department",
  "Deli Department",
  "Beer & Wine",
];
export const PROVINCE_OPTIONS = [
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "NT",
  "NU",
  "ON",
  "PE",
  "QC",
  "SK",
  "YT",
];

export const getDeviceType = (): "mobile" | "tablet" | "desktop" => {
  if (typeof window === "undefined") return "desktop";

  const width = window.innerWidth;

  if (width <= 767) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
};

export const formatPhoneNumber = (phone: string) => {
  if (!phone || phone.length !== 10) return "N/A";
  const areaCode = phone.slice(0, 3);
  const prefix = phone.slice(3, 6);
  const lineNumber = phone.slice(6);
  return `+(${areaCode}) ${prefix}-${lineNumber}`;
};

const provinceAbbreviations: Record<string, string> = {
  "Alberta (AB)": "AB",
  "British Columbia (BC)": "BC",
  "Manitoba (MB)": "MB",
  "New Brunswick (NB)": "NB",
  "Newfoundland and Labrador (NL)": "NL",
  "Nova Scotia (NS)": "NS",
  "Northwest Territories (NT)": "NT",
  "Nunavut  (NU)": "NU",
  "Ontario (ON)": "ON",
  "Prince Edward Island (PE)": "PE",
  "Quebec (QC)": "QC",
  "Saskatchewan (SK)": "SK",
  "Yukon (YT)": "YT",
};

// Reverse the mapping: { AB: "Alberta", ... }
const abbreviationToProvince = Object.fromEntries(
  Object.entries(provinceAbbreviations).map(([full, abbr]) => [abbr, full])
);

// Function to convert abbreviation list to full names
export const formatProvinceFullNames = (abbreviationList: string[]) => {
  return abbreviationList.map((abbr) => {
    const fullName = abbreviationToProvince[abbr] || abbr;
    return { optionval: abbr, optionname: fullName };
  });
};

export function getStoreStatus(
  storeHours: { [key: string]: string } | null | undefined,
  provinceCode: string,
  date: Date = new Date()
): string {
  const timeZone = provinceTimeZones[provinceCode] || "America/Toronto";
  if (
    !storeHours ||
    typeof storeHours !== "object" ||
    Object.keys(storeHours).length === 0
  ) {
    return "Closed";
  }

  const currentDateTime = moment(date).tz(timeZone);

  const currentDay = daysOfWeek[currentDateTime.day()];
  const currentTimeInMinutes =
    currentDateTime.hour() * 60 + currentDateTime.minute();

  const hoursForDay = storeHours[currentDay]?.trim();
  if (!hoursForDay || hoursForDay.toLowerCase() === "closed") {
    return "Closed now";
  }

  const [openingTime, closingTime] = hoursForDay.split(" to ");

  if (!openingTime || !closingTime) {
    return "Closed now";
  }

  const openingMinutes = parseTimeToMinutes(openingTime);
  let closingMinutes = parseTimeToMinutes(closingTime);

  if (openingMinutes === -1 || closingMinutes === -1) {
    return "Closed now";
  }

  // Handle midnight case (e.g., "7:00 AM to 12:00 AM")
  if (closingMinutes === 0 && openingMinutes !== 0) {
    closingMinutes = 1440;
  }

  const isOpen =
    currentTimeInMinutes >= openingMinutes &&
    currentTimeInMinutes <= closingMinutes;

  const formatTime = (time: string): string =>
    time.replace(/\./g, "").replace(/\s+/g, "").toUpperCase();

  return isOpen ? `Open until ${formatTime(closingTime)}` : "Closed now";
}

export function getStoreStatusWithRange(
  storeHours: { [key: string]: string } | null | undefined,
  provinceCode: string,
  date: Date = new Date()
): string {
  const timeZone = provinceTimeZones[provinceCode] || "America/Toronto";

  if (
    !storeHours ||
    typeof storeHours !== "object" ||
    Object.keys(storeHours).length === 0
  ) {
    return "Closed";
  }

  const currentDateTime = moment(date).tz(timeZone);
  const currentDay = daysOfWeek[currentDateTime.day()];
  const currentTimeInMinutes =
    currentDateTime.hour() * 60 + currentDateTime.minute();

  const hoursForDay = storeHours[currentDay]?.trim();
  if (!hoursForDay || hoursForDay.toLowerCase() === "closed") {
    return "Closed now";
  }

  const [openingTime, closingTime] = hoursForDay.split(" to ");
  if (!openingTime || !closingTime) return "Closed now";

  const openingMinutes = parseTimeToMinutesNew(openingTime);
  let closingMinutes = parseTimeToMinutesNew(closingTime);

  if (openingMinutes === -1 || closingMinutes === -1) return "Closed now";

  // Handle "12:00 AM" midnight case
  if (closingMinutes === 0 && openingMinutes !== 0) {
    closingMinutes = 1440;
  }

  const isOpen =
    currentTimeInMinutes >= openingMinutes &&
    currentTimeInMinutes <= closingMinutes;

  const cleanedOpeningTime = cleanTimeFormat(openingTime);
  const cleanedClosingTime = cleanTimeFormat(closingTime);

  return isOpen
    ? `Open today, ${cleanedOpeningTime} - ${cleanedClosingTime}`
    : "Closed now";
}

// Clean time format: "01:00 PM" → "1:00 PM"
function cleanTimeFormat(timeStr: string): string {
  const parsed = moment(timeStr, ["h:mm A", "hh:mm A"], true);
  return parsed.isValid() ? parsed.format("h:mm A") : timeStr;
}

// Example helper
function parseTimeToMinutesNew(timeStr: string): number {
  const m = moment(timeStr, ["h:mm A", "hh:mm A"], true);
  return m.isValid() ? m.hours() * 60 + m.minutes() : -1;
}
